import { AnimatePresence, motion } from "framer-motion";
import { observer } from "mobx-react-lite";
import { useState } from "react";

import { IPlayground, usePlayground } from "../../../playground-store";

import useExtraControls from "./hooks/useExtraControls";
import MessageWithRun from "./MessageWithRun";
import OutputHeader from "./OutputHeader";
import RewindTemplateDialog from "./RewindTemplateDialog";

import ErrorDisplay from "@/components/ui/ErrorDisplay";
import InlineWarning from "@/components/InlineWarning";
import { displayErrorToast } from "@/utils/toast";
import { useDeleteRunGroup } from "@/queries";
import { useAuth } from "@/context/auth-context";

interface OutputCardProps {
  output: IPlayground;
  outputNumber?: number;
  isCollapsed?: boolean;
  onCollapsedChange?: (isCollapsed: boolean) => void;
}

const normalizeRawInputVariableSets = (sets: IPlayground["raw_input_variable_sets"]) =>
  sets.length === 1 && sets.filter(set => set.length).length === 0 ? [] : sets;

const OutputCard = ({
  output,
  outputNumber,
  isCollapsed: controlledIsCollapsed,
  onCollapsedChange,
}: OutputCardProps) => {
  const playground = usePlayground();
  const userToken = useAuth()?.userToken ?? "";
  const deleteRunGroup = useDeleteRunGroup(userToken);
  const [uncontrolledIsCollapsed, setUncontrolledIsCollapsed] = useState(false);
  const isCollapsed = controlledIsCollapsed ?? uncontrolledIsCollapsed;
  const [isRewindDialogOpen, setIsRewindDialogOpen] = useState(false);
  const extraControls = useExtraControls(output, () => setIsRewindDialogOpen(true));

  const hasDifferentState =
    JSON.stringify(output.rewindBlueprint) !== JSON.stringify(playground.blueprint) ||
    JSON.stringify(normalizeRawInputVariableSets(output.raw_input_variable_sets)) !==
      JSON.stringify(normalizeRawInputVariableSets(playground.raw_input_variable_sets));

  const genericRun = Object.values(output.currentOperation?.sets || {}).find(set => set.run)?.run;

  const dateStarted = genericRun?.created_at;

  const handleToggleCollapse = () => {
    const nextIsCollapsed = !isCollapsed;
    onCollapsedChange?.(nextIsCollapsed);

    if (controlledIsCollapsed === undefined) {
      setUncontrolledIsCollapsed(nextIsCollapsed);
    }
  };

  const onDelete = () => {
    const operationId = output.currentOperation!.id;

    deleteRunGroup.mutate(
      {
        workspaceId: playground.workspace_id,
        runGroupId: operationId,
      },
      {
        onSuccess: () => playground.deleteOutput(output),
        onError: () => displayErrorToast("Error deleting output:"),
      },
    );
  };

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200/70 bg-white/95 shadow-none ring-1 ring-black/[0.02] transition-colors duration-200 hover:border-gray-300/80">
      <OutputHeader
        provider={output.provider ?? ""}
        modelName={output.model_name_and_model_config_display_name}
        outputNumber={outputNumber}
        createdAt={dateStarted}
        onDelete={onDelete}
        isCollapsed={isCollapsed}
        onToggleCollapse={handleToggleCollapse}
      />
      <AnimatePresence initial={false}>
        {!isCollapsed && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{
              type: "spring",
              duration: 0.3,
              bounce: 0,
            }}
            className="overflow-hidden"
          >
            <div className="flex h-full w-full flex-col gap-y-2 bg-gray-50/60 px-2.5 py-2.5">
              <div className="flex h-full w-full flex-row gap-2 overflow-x-auto pb-1">
                {output.outputMessages.map((message, index) => {
                  const request_id = output.getMessageRun(message.id)?.request_id;
                  const inputSet = Object.values(output.currentOperation?.sets || {}).find(
                    set => set.message_id === message.id,
                  );
                  const inputSetName =
                    inputSet?.name ||
                    output.input_variable_sets[index]?.name ||
                    `Input set ${index + 1}`;
                  const showEmptyMessageWarning = request_id
                    ? !!output.getMessage(message.id)?.isEmptyMessage
                    : false;

                  return (
                    <div
                      key={`${message.id}`}
                      className={`${
                        output.outputMessages.length === 1 ? "w-full" : "w-full flex-1"
                      } min-w-[350px] flex-shrink-0`}
                    >
                      {output.outputMessages.length > 1 && (
                        <div className="mb-1.5 truncate px-1 text-xs font-medium text-gray-500">
                          {inputSetName}
                        </div>
                      )}
                      <InlineWarning show={showEmptyMessageWarning} />
                      <MessageWithRun
                        message={message}
                        getMessageRun={output.getMessageRun}
                        isMessageCompleted={output.isMessageCompleted}
                        extraControls={extraControls}
                      />
                    </div>
                  );
                })}
              </div>
              {Object.values(output.currentOperation?.sets || {}).some(set => set.error) && (
                <div className="flex h-full w-full flex-col gap-y-2 p-2">
                  {Object.values(output.currentOperation?.sets || {})
                    .filter(set => set.error)
                    .map(set => (
                      <ErrorDisplay>{set.error}</ErrorDisplay>
                    ))}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      <RewindTemplateDialog
        output={output}
        isOpen={isRewindDialogOpen}
        hasDifferentState={hasDifferentState}
        onOpenChange={setIsRewindDialogOpen}
        onConfirmRewind={() => playground.rewind(output)}
      />
    </div>
  );
};

export default observer(OutputCard);
